import { X } from 'lucide-react';
import { useEffect, useId, useRef, type ReactNode } from 'react';

import { cn } from '../../lib/cn.ts';
import { Button } from './button.tsx';

/**
 * Modal dialog on the native <dialog> element (showModal): the browser traps focus,
 * closes on Esc and makes the page behind inert. No injected <style> tags, so it
 * works with the strict CSP. `side` turns it into a side sheet (mobile menu).
 */
export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  side = false,
  brand = false,
  className,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: ReactNode;
  children: ReactNode;
  side?: boolean;
  /** Brand header (gradient, white text): the mobile menu, like the desktop sidebar. */
  brand?: boolean;
  className?: string;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const descriptionId = useId();

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      aria-describedby={description ? descriptionId : undefined}
      onClose={onClose}
      onClick={(event) => {
        // A click on the backdrop (the dialog itself, outside its content) closes it.
        if (event.target === event.currentTarget) onClose();
      }}
      className={cn(
        'bg-surface text-foreground shadow-xl',
        side
          ? 'm-0 h-dvh max-h-dvh w-[min(20rem,85vw)] max-w-none'
          : 'm-auto w-[min(32rem,calc(100vw-2rem))] rounded-xl border border-border max-sm:mb-0 max-sm:w-full max-sm:max-w-none max-sm:rounded-b-none',
        className,
      )}
    >
      <div
        className={cn(
          'flex items-start justify-between gap-4 p-4',
          brand ? 'bg-brand' : 'border-b border-border',
        )}
      >
        <div>
          <h2 id={titleId} className="text-lg font-semibold">
            {title}
          </h2>
          {description && (
            <p id={descriptionId} className="mt-1 text-sm text-muted-foreground">
              {description}
            </p>
          )}
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          aria-label="Cerrar"
          className={cn(brand && 'text-white hover:bg-white/15')}
        >
          <X />
        </Button>
      </div>
      <div className="p-4">{children}</div>
    </dialog>
  );
}
